import { formatDateTime, type Locale, useTranslation } from '@multaqa/i18n'
import type { Dispatch, SetStateAction } from 'react'
import type { CompanyOption, CompanyResource } from './api'
import {
  CompanyCurrencyField,
  CompanyParentField,
  CompanyReadOnlyField,
  CompanySelectField,
  CompanyTextField,
} from './company-form-fields'
import { parentName } from './company-formatters'

type FormValues = {
  legal_name_en: string
  legal_name_ar: string
  trading_name_en: string
  trading_name_ar: string
  code: string
  registration_number: string
  tax_identifier: string
  default_locale: string
  fiscal_year_start_month: string
  parent_id: string
}

export function CompanyOverviewForm({
  values,
  setValues,
  company,
  candidates,
  locale,
  optionsLoading,
  optionsError,
  onRetry,
  onSubmit,
}: {
  values: FormValues
  setValues: Dispatch<SetStateAction<FormValues>>
  company: CompanyResource
  candidates: CompanyOption[]
  locale: string
  optionsLoading: boolean
  optionsError: boolean
  onRetry: () => void
  onSubmit: () => void
}) {
  const { t } = useTranslation()
  const update = (field: keyof FormValues, value: string) =>
    setValues((current) => ({ ...current, [field]: value }))
  return (
    <form
      id="company-overview-form"
      className="grid gap-4 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
      noValidate
    >
      <CompanyTextField
        id="company-overview-legal-name-en"
        label={t('companies.form.legalNameEn')}
        value={values.legal_name_en}
        dir="ltr"
        onChange={(value) => update('legal_name_en', value)}
      />
      <CompanyTextField
        id="company-overview-legal-name-ar"
        label={t('companies.form.legalNameAr')}
        value={values.legal_name_ar}
        dir="rtl"
        onChange={(value) => update('legal_name_ar', value)}
      />
      <CompanyTextField
        id="company-overview-trading-name-en"
        label={t('companies.form.tradingNameEn')}
        value={values.trading_name_en}
        dir="ltr"
        onChange={(value) => update('trading_name_en', value)}
      />
      <CompanyTextField
        id="company-overview-trading-name-ar"
        label={t('companies.form.tradingNameAr')}
        value={values.trading_name_ar}
        dir="rtl"
        onChange={(value) => update('trading_name_ar', value)}
      />
      <CompanyTextField
        id="company-overview-code"
        label={t('companies.form.code')}
        value={values.code}
        dir="ltr"
        onChange={(value) => update('code', value)}
      />
      <CompanyReadOnlyField label={t('companies.form.country')} value={company.country} />
      <CompanyCurrencyField value={company.base_currency} />
      <CompanyTextField
        id="company-overview-registration-number"
        label={t('companies.form.registrationNumber')}
        value={values.registration_number}
        dir="ltr"
        onChange={(value) => update('registration_number', value)}
      />
      <CompanyTextField
        id="company-overview-tax-identifier"
        label={t('companies.form.taxIdentifier')}
        value={values.tax_identifier}
        dir="ltr"
        onChange={(value) => update('tax_identifier', value)}
      />
      <CompanySelectField
        id="company-overview-default-locale"
        label={t('companies.form.defaultLocale')}
        value={values.default_locale}
        options={[
          { value: 'en', label: t('companies.form.localeEnglish') },
          { value: 'ar', label: t('companies.form.localeArabic') },
        ]}
        onChange={(value) => update('default_locale', value)}
      />
      <CompanySelectField
        id="company-overview-fiscal-year-start"
        label={t('companies.form.fiscalYearStart')}
        value={values.fiscal_year_start_month}
        options={Array.from({ length: 12 }, (_, index) => {
          const month = String(index + 1)
          return { value: month, label: t(`companies.form.month.${month}`) }
        })}
        onChange={(value) => update('fiscal_year_start_month', value)}
      />
      <CompanyParentField
        value={values.parent_id}
        options={candidates}
        locale={locale}
        loading={optionsLoading}
        error={optionsError}
        onRetry={onRetry}
        onChange={(value) => update('parent_id', value)}
      />
    </form>
  )
}

export function CompanyOverviewValues({
  company,
  options,
  locale,
}: {
  company: CompanyResource
  options: CompanyOption[]
  locale: Locale
}) {
  const { t } = useTranslation()
  const empty = t('companies.common.empty')
  return (
    <div className="grid gap-x-6 sm:grid-cols-2" data-testid="company-overview-view">
      <CompanyReadOnlyField
        label={t('companies.form.legalNameEn')}
        value={company.legal_name.en ?? empty}
        dir="ltr"
      />
      <CompanyReadOnlyField
        label={t('companies.form.legalNameAr')}
        value={company.legal_name.ar ?? empty}
        dir="rtl"
      />
      <CompanyReadOnlyField
        label={t('companies.form.tradingNameEn')}
        value={company.trading_name?.en ?? empty}
        dir="ltr"
      />
      <CompanyReadOnlyField
        label={t('companies.form.tradingNameAr')}
        value={company.trading_name?.ar ?? empty}
        dir="rtl"
      />
      <CompanyReadOnlyField
        label={t('companies.form.code')}
        value={company.code ?? empty}
        dir="ltr"
      />
      <CompanyReadOnlyField label={t('companies.form.country')} value={company.country} />
      <CompanyCurrencyField value={company.base_currency} />
      <CompanyReadOnlyField
        label={t('companies.form.registrationNumber')}
        value={company.registration_number ?? empty}
        dir="ltr"
      />
      <CompanyReadOnlyField
        label={t('companies.form.taxIdentifier')}
        value={company.tax_identifier ?? empty}
        dir="ltr"
      />
      <CompanyReadOnlyField
        label={t('companies.form.defaultLocale')}
        value={company.default_locale}
        dir="ltr"
      />
      <CompanyReadOnlyField
        label={t('companies.form.fiscalYearStart')}
        value={t(`companies.form.month.${company.fiscal_year_start_month}`)}
      />
      <CompanyReadOnlyField
        label={t('companies.form.parent')}
        value={parentName(
          company.parent_id,
          options,
          locale,
          t('companies.form.parentNone'),
          empty,
        )}
      />
      <CompanyReadOnlyField
        label={t('companies.status.label')}
        value={statusLabel(company.status, t)}
      />
      <CompanyReadOnlyField
        label={t('companies.form.createdAt')}
        value={company.created_at ? formatDateTime(new Date(company.created_at), locale) : empty}
        dir="ltr"
      />
    </div>
  )
}

function statusLabel(status: string, t: (key: string) => string): string {
  if (status === 'active' || status === 'suspended') return t(`companies.status.${status}`)
  return status
}
